import { useEffect, useState } from 'react';

import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

import { isOptionActive, useAudioInputs, useMeetingManager, useMeetingStatus, useToggleLocalMute } from 'amazon-chime-sdk-component-library-react';

import ControlButton from './control-button';
import CustomPopover, { CustomPopoverItemProps } from './custom-popover';
import { Divider } from '@mui/material';
import { findDeviceFromList } from '../../../video/controls/select-camera-popover';

interface MuteProps {
    startMuted: boolean;
    disabled?: boolean;
    preferredMicList?: string[]
    onMicFound?: (found: string | undefined) => void;
}

const DropdownMuteButton: React.FC<MuteProps> = ({
    startMuted,
    disabled = false,
    preferredMicList,
    onMicFound,
}) => {

    const meetingManager = useMeetingManager();
    const { muted, toggleMute } = useToggleLocalMute();
    const { devices, selectedDevice } = useAudioInputs();
    const [dropdownOptions, setDropdownOptions] = useState<CustomPopoverItemProps[]>([]);

    const [defaultMicChecked, setDefaultMicChecked] = useState<boolean>(false)

    const status = useMeetingStatus();

    useEffect(() => {
        if (!preferredMicList) {
            return
        }

        if (status != 1) {
            return;
        }

        if (defaultMicChecked) {
            return;
        }

        const result = findDeviceFromList(devices, preferredMicList)

        if (result.foundDevice) {
            handleClick(result.foundDevice)
        }

        if (onMicFound) {
            onMicFound(result.foundDeviceName)
        }

        setDefaultMicChecked(true)

    }, [status])

    useEffect(() => {
        if (status != 1) {
            console.log("Not connected");
            return;
        }

        if (muted !== startMuted) {
            toggleMute();
        }

    }, [startMuted, status]);

    const selectOption = (option: CustomPopoverItemProps) => {
        handleClick(option.id);
    }

    const handleClick = async (deviceId: string): Promise<void> => {
        try {
            await meetingManager.startAudioInputDevice(deviceId);
        } catch (error) {
            console.error('AudioInputControl failed to select audio input device');
        }
    };

    useEffect(() => {
        const getDropdownOptions = async (): Promise<void> => {
            const deviceList: CustomPopoverItemProps[] = [];

            devices.map(async (device) => {
                const popoverItem: CustomPopoverItemProps = {
                    label: device.label,
                    id: device.deviceId,
                    active: await isOptionActive(device, device.deviceId)
                };

                deviceList.push(popoverItem);
            });

            setDropdownOptions(deviceList);
        };

        getDropdownOptions();
    }, [
        devices,
        selectedDevice,
        meetingManager,
        meetingManager.startAudioInputDevice,
    ]);

    const toggleAudioAndSentMessage = () => {
        toggleMute();
    }

    return (
        <ControlButton
            activeIcon={<MicIcon color='action' />}
            inactiveIcon={<MicOffIcon color='action' />}
            active={!muted}
            onClick={toggleAudioAndSentMessage}
            showText={false}
            disabled={disabled}
            activeLabel={'Mute Myself'}
            inactiveLabel={'Unmute Myself'} >
            <Divider orientation="vertical" />
            <CustomPopover
                options={dropdownOptions}
                openPopOverLabel='Select Microphone'
                selectedOption={selectedDevice?.toString() || ""}
                setSelectedOption={selectOption} />
        </ControlButton >
    );
};

export default DropdownMuteButton;